import SeoHead from '@atoms/SeoHead';
import { getAllCategories } from '@lib/sanity';
import Layout from '@molecules/Layout';
import type { LayoutProps } from '@molecules/Layout/Layout.types';
import { getClient } from '@sanity/lib/getClient';
import type { GetStaticProps } from 'next';
import type { ReactElement } from 'react';

import { mapLayout } from '@/mappers/layout';
import { ContentWrapper, GeneralPageWrapper } from '@/styles/GlobalStyle';

interface PrivacyPageProps {
  layoutData: Omit<LayoutProps, 'children'>;
}

const PrivacyPolicy = ({ layoutData }: PrivacyPageProps): ReactElement => (
  <>
    <SeoHead title="Polityka prywatności" noIndex />
    <Layout {...layoutData}>
      <GeneralPageWrapper>
        <ContentWrapper>
          <h1>Polityka prywatności</h1>
          <p>
            Strona ta jest blogiem podróżniczym i wykorzystuje pliki cookies w
            celu zbierania informacji statystycznych o użytkownikach oraz
            wyświetla reklamy.
          </p>
          <h2>Zbieranie i przetwarzanie danych osobowych</h2>
          <p>
            Podczas korzystania ze strony internetowej &quot;Fox Paradox&quot;,
            użytkownik może zostać poproszony o podanie niektórych danych
            osobowych, takich jak imię i nazwisko, adres e-mail lub inne dane
            kontaktowe. Dane te będą przetwarzane jedynie w celu skontaktowania
            się z użytkownikiem w odpowiedzi na jego zapytania lub w celu
            dostarczenia usług oferowanych na stronie.
          </p>
          <h2>Pliki cookies</h2>
          <p>
            Strona internetowa &quot;Fox Paradox&quot; wykorzystuje pliki
            cookies w celu zapewnienia lepszego doświadczenia użytkowania oraz
            zbierania informacji o tym, jak użytkownicy korzystają z witryny.
            Pliki cookies to małe pliki tekstowe umieszczane na komputerze
            użytkownika, które pomagają zapamiętać preferencje użytkowników i
            śledzić, jakie strony są odwiedzane na naszej stronie. Informacje te
            wykorzystujemy do analizy statystycznej ruchu na stronie oraz do
            personalizacji zawartości dla użytkowników.
          </p>
          <h2>Reklamy</h2>
          <p>
            Na stronie internetowej &quot;Fox Paradox&quot; mogą pojawiać się
            reklamy. Reklamy te są wyświetlane przez firmy trzecie, które
            wykorzystują pliki cookies do zbierania informacji o preferencjach
            użytkowników w celu dostarczenia reklam odpowiadających ich
            zainteresowaniom. Strona &quot;Fox Paradox&quot; nie ma kontroli nad
            tymi plikami cookies i nie ponosi odpowiedzialności za praktyki
            prywatności firm trzecich.
          </p>
          <h2>Newsletter i mapa Teneryfy</h2>
          <p>
            Na stronie „Darmowa mapa Teneryfy” (/sklep/teneryfa/mapa) użytkownik
            może zapisać się, aby otrzymać bezpłatną mapę miejsc na Teneryfie.
            Wymaga to podania adresu e-mail oraz wyrażenia odrębnej, dobrowolnej
            zgody na otrzymywanie treści marketingowych i ofert Fox Paradox,
            niekoniecznie ograniczonych tematycznie do Teneryfy. Podstawą prawną
            przetwarzania jest zgoda osoby, której dane dotyczą (art. 6 ust. 1
            lit. a RODO).
          </p>
          <p>
            Adres e-mail jest przekazywany do MailerLite — dostawcy usługi
            mailingowej, który przechowuje go w celu wysyłki wiadomości z mapą
            oraz późniejszych kampanii marketingowych Fox Paradox. Dane są
            przechowywane do momentu wycofania zgody lub wypisania się z listy
            mailingowej.
          </p>
          <p>
            Zgodę na przetwarzanie danych w celach marketingowych można wycofać
            w dowolnym momencie — poprzez link &quot;wypisz się&quot; znajdujący
            się w każdej otrzymanej wiadomości e-mail lub kontaktując się z nami
            pod adresem kontakt@foxparadox.pl. Wycofanie zgody nie wpływa na
            zgodność z prawem przetwarzania dokonanego przed jej wycofaniem.
          </p>
          <p>
            Zapisanie się na liście jest dodatkowo rejestrowane jako zdarzenie
            statystyczne (konwersja) za pomocą Google Tag Manager, zgodnie z
            zasadami opisanymi w sekcji „Pliki cookies”.
          </p>
          <h2>Ochrona danych osobowych</h2>
          <p>
            Strona &quot;Fox Paradox&quot; zobowiązuje się do ochrony danych
            osobowych przed nieautoryzowanym dostępem, utratą lub zniszczeniem.
            Wykorzystujemy odpowiednie środki techniczne i organizacyjne w celu
            ochrony Państwa danych osobowych.
          </p>
          <h2>Prawa użytkowników</h2>
          <p>
            Zgodnie z obowiązującymi przepisami, użytkownicy strony internetowej
            &quot;Fox Paradox&quot; mają prawo do dostępu do swoich danych
            osobowych, do ich poprawiania, usunięcia lub ograniczenia
            przetwarzania, do cofnięcia w dowolnym momencie wyrażonej zgody, a
            także do wniesienia skargi do organu nadzorczego.
          </p>
          <h2>Zmiany w polityce prywatności</h2>
          <p>
            Polityka prywatności dla strony internetowej &quot;Fox Paradox&quot;
            może być okresowo aktualizowana. Każda zmiana polityki prywatności
            zostanie opublikowana na tej stronie wraz z datą ostatniej
            aktualizacji. Zachęcamy użytkowników do regularnego sprawdzania tej
            strony, aby być na bieżąco z naszą polityką prywatności.
          </p>
          <h2>Kontakt</h2>
          <p>
            Jeśli mają Państwo jakieś pytania lub wątpliwości dotyczące polityki
            prywatności na stronie &quot;Fox Paradox&quot;, prosimy o kontakt z
            właścicielem strony poprzez adres e-mail: kontakt@foxparadox.pl.
          </p>
        </ContentWrapper>
      </GeneralPageWrapper>
    </Layout>
  </>
);

export const getStaticProps: GetStaticProps<PrivacyPageProps> = async () => {
  const client = getClient();

  const tags = await getAllCategories(client);
  const layoutData = mapLayout(tags);

  return {
    props: {
      layoutData,
    },
  };
};

export default PrivacyPolicy;
